import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Clock, Lock, CheckCircle2, XCircle, CircleDashed, Loader2, Flag } from "lucide-react";
import { api, errMsg, useSeo, QUESTION_CATS, fmtDate } from "../lib/api";
import { useAuth } from "../context/AppContext";
import { PageHero, Section, Loading, Empty, Badge } from "../components/Common";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

export function ExamList() {
  const [exams, setExams] = useState(null);
  const { user } = useAuth();
  useSeo("Deneme Sınavları", "Özel güvenlik deneme sınavı – ücretsiz örnek sınav, süreli test, otomatik puanlama ve doğru cevap açıklamaları.");
  useEffect(() => { api.get("/exams").then((r) => setExams(r.data)); }, []);
  return (
    <>
      <PageHero eyebrow="Online Sınav" title="Deneme Sınavları" text="Sorular kurumumuz tarafından hazırlanmış örnek deneme sorularıdır; resmî sınav soruları değildir." />
      <Section>
        {!exams ? <Loading /> : (
          <div className="grid gap-4 md:grid-cols-2">
            {exams.map((e) => (
              <div key={e.id} className="card-ada p-6 flex flex-col" data-testid={`exam-card-${e.id}`}>
                <div className="flex gap-2 mb-3">{e.requires_login ? <Badge tone="dark">Üyelere özel</Badge> : <Badge tone="green">Ücretsiz • üyeliksiz</Badge>}<Badge>Örnek deneme soruları</Badge></div>
                <h2 className="font-black text-xl">{e.title}</h2>
                <p className="text-sm text-zinc-600 mt-2 flex-1">{e.description}</p>
                <p className="text-xs text-zinc-500 mt-4">{e.question_count} soru • {e.time_limit_minutes ? `${e.time_limit_minutes} dakika` : "Süre sınırı yok"} • Geçme: %{e.pass_percent}</p>
                {e.requires_login && !user ? (
                  <Link to="/kursiyer-girisi" className="btn-dark mt-4" data-testid={`exam-login-${e.id}`}><Lock size={16} />Giriş yaparak başla</Link>
                ) : <Link to={`/deneme-sinavlari/${e.id}`} className="btn-red mt-4" data-testid={`exam-start-${e.id}`}>Sınava Başla</Link>}
              </div>
            ))}
          </div>
        )}
        {user && <p className="mt-6 text-sm"><Link to="/kursiyer-paneli?tab=sinavlar" className="underline font-semibold" data-testid="exam-history-link">Önceki sınav sonuçlarım ve gelişimim →</Link></p>}
      </Section>
    </>
  );
}

function Timer({ expires, onEnd }) {
  const [left, setLeft] = useState(() => Math.max(0, new Date(expires) - Date.now()));
  useEffect(() => {
    const t = setInterval(() => {
      const l = Math.max(0, new Date(expires) - Date.now());
      setLeft(l);
      if (l <= 0) { clearInterval(t); onEnd(); }
    }, 1000);
    return () => clearInterval(t);
  }, [expires, onEnd]);
  const m = Math.floor(left / 60000), s = Math.floor((left % 60000) / 1000);
  return <span className={`inline-flex items-center gap-1.5 font-display font-black text-lg ${left < 60000 ? "text-[#dc2626]" : ""}`} data-testid="exam-timer"><Clock size={18} />{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}</span>;
}

export function Result({ r }) {
  const icon = { correct: <CheckCircle2 className="text-green-600" size={18} />, wrong: <XCircle className="text-[#dc2626]" size={18} />, blank: <CircleDashed className="text-zinc-400" size={18} /> };
  return (
    <div data-testid="exam-result">
      <div className={`p-6 sm:p-8 text-white ${r.passed ? "bg-zinc-950" : "bg-[#b91c1c]"}`}>
        <p className="text-sm uppercase tracking-widest">{r.exam_title}</p>
        <p className="font-display font-black text-6xl mt-2" data-testid="exam-result-percent">%{r.percent}</p>
        <p className="mt-1 font-bold" data-testid="exam-result-status">{r.passed ? "Başarılı" : "Başarı sınırının altında"} • Puan {r.score}/{r.max_score}</p>
        <div className="mt-6 grid grid-cols-3 gap-px bg-white/20 text-center">
          {[["Doğru", r.correct, "correct"], ["Yanlış", r.wrong, "wrong"], ["Boş", r.blank, "blank"]].map(([l, v, k]) => <div key={l} className="bg-black/20 py-3"><p className="text-2xl font-black" data-testid={`exam-result-${k}`}>{v}</p><p className="text-xs uppercase">{l}</p></div>)}
        </div>
        {r.late && <p className="mt-3 text-xs">Not: Sınav süre bitiminden sonra teslim edildi.</p>}
      </div>
      <h3 className="font-black text-lg mt-8 mb-4">Cevap anahtarı ve açıklamalar</h3>
      <div className="grid gap-3">
        {r.details.map((d, i) => (
          <div key={d.question_id} className="card-ada p-5" data-testid={`review-${i}`}>
            <p className="flex gap-2 font-bold">{icon[d.state]}<span>{i + 1}. {d.text}</span></p>
            <ul className="mt-3 grid gap-1.5 text-sm">
              {d.options.map((o) => <li key={o.key} className={`px-3 py-2 border ${o.key === d.correct_index ? "border-green-600 bg-green-50" : o.key === d.selected ? "border-[#dc2626] bg-red-50" : "border-zinc-200"}`}>{o.text}{o.key === d.correct_index && " ✓"}</li>)}
            </ul>
            {d.state !== "correct" && d.explanation && <p className="mt-3 text-sm text-zinc-600 border-l-4 border-zinc-900 pl-3">{d.explanation}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function ExamRun() {
  const { id } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const [cur, setCur] = useState(0);
  const [ans, setAns] = useState({});
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const ansRef = useRef(ans);
  ansRef.current = ans;
  useSeo("Deneme Sınavı", "Online özel güvenlik deneme sınavı");
  useEffect(() => { api.post(`/exams/${id}/start`).then((r) => setS(r.data)).catch((e) => setErr(errMsg(e))); }, [id]);
  const submit = useCallback(async () => {
    if (!s || busy) return;
    setBusy(true); setConfirm(false);
    try {
      const { data } = await api.post(`/exam-sessions/${s.session_id}/submit`, { token: s.token, answers: ansRef.current });
      setResult(data); window.scrollTo(0, 0);
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  }, [s, busy]);
  if (err) return <Section><Empty text={err} testid="exam-error" /><div className="mt-4 flex gap-2"><Link to="/deneme-sinavlari" className="btn-dark">Sınavlara dön</Link><Link to="/kursiyer-girisi" className="btn-red">Giriş yap</Link></div></Section>;
  if (!s) return <Loading />;
  if (result) return <Section><Result r={result} /><div className="mt-6 flex gap-2"><button className="btn-red" onClick={() => nav(0)} data-testid="exam-retry-btn">Yeniden Çöz</button><Link to="/deneme-sinavlari" className="btn-dark">Sınavlar</Link></div></Section>;
  const q = s.questions[cur];
  const blanks = s.questions.filter((x) => ans[x.id] === undefined).length;
  return (
    <Section>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 border-b-2 border-zinc-950 pb-4">
        <div><p className="text-xs uppercase tracking-widest text-zinc-500">Örnek deneme soruları</p><h1 className="font-black text-xl sm:text-2xl">{s.exam.title}</h1></div>
        {s.expires_at && <Timer expires={s.expires_at} onEnd={submit} />}
      </div>
      <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
        <div className="card-ada p-5 sm:p-8" data-testid="exam-question">
          <p className="text-xs font-bold text-[#dc2626] uppercase">Soru {cur + 1} / {s.questions.length} • {QUESTION_CATS[q.category] || q.category} • {q.points} puan</p>
          <h2 className="mt-3 text-lg sm:text-xl font-bold leading-snug" data-testid="exam-question-text">{q.text}</h2>
          <div className="mt-6 grid gap-2" role="radiogroup">
            {q.options.map((o, i) => (
              <button key={o.key} role="radio" aria-checked={ans[q.id] === o.key} onClick={() => setAns({ ...ans, [q.id]: o.key })} data-testid={`exam-option-${i}`}
                className={`text-left px-4 py-3.5 border-2 text-sm font-medium flex gap-3 ${ans[q.id] === o.key ? "border-[#dc2626] bg-red-50" : "border-zinc-200 hover:border-zinc-900"}`} style={{ transition: "border-color .15s" }}>
                <span className="font-display font-black">{"ABCDEFGH"[i]})</span>{o.text}
              </button>
            ))}
          </div>
          <div className="mt-8 flex justify-between gap-2">
            <button className="btn-dark" disabled={cur === 0} onClick={() => setCur(cur - 1)} data-testid="exam-prev-btn">Önceki</button>
            {ans[q.id] !== undefined && <button className="text-sm underline" onClick={() => { const a = { ...ans }; delete a[q.id]; setAns(a); }} data-testid="exam-clear-btn">Cevabı temizle</button>}
            {cur < s.questions.length - 1 ? <button className="btn-red" onClick={() => setCur(cur + 1)} data-testid="exam-next-btn">Sonraki</button> : <button className="btn-red" onClick={() => setConfirm(true)} data-testid="exam-finish-btn"><Flag size={16} />Bitir</button>}
          </div>
        </div>
        <aside className="card-ada p-5 h-fit">
          <p className="text-sm font-bold mb-3">Soru haritası <span className="text-zinc-500 font-normal">({blanks} boş)</span></p>
          <div className="grid grid-cols-6 gap-1.5">
            {s.questions.map((x, i) => <button key={x.id} onClick={() => setCur(i)} data-testid={`exam-nav-${i}`} className={`h-10 text-sm font-bold border ${i === cur ? "ring-2 ring-[#dc2626]" : ""} ${ans[x.id] !== undefined ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300"}`}>{i + 1}</button>)}
          </div>
          <button className="btn-red w-full mt-5" onClick={() => setConfirm(true)} disabled={busy} data-testid="exam-submit-open-btn">{busy && <Loader2 size={16} className="animate-spin" />}Sınavı Teslim Et</button>
        </aside>
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="exam-confirm-dialog">
          <AlertDialogHeader><AlertDialogTitle>Sınavı bitirmek istiyor musunuz?</AlertDialogTitle>
            <AlertDialogDescription>{blanks ? `${blanks} soruyu boş bıraktınız. ` : "Tüm soruları cevapladınız. "}Teslim ettikten sonra cevaplarınızı değiştiremezsiniz.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="exam-confirm-cancel">Sınava dön</AlertDialogCancel><AlertDialogAction onClick={submit} className="bg-[#dc2626] hover:bg-[#b91c1c]" data-testid="exam-confirm-submit">Teslim Et</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Section>
  );
}

export function ResultView() {
  const { sid } = useParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/exam-sessions/${sid}`).then((x) => setR(x.data)).catch((e) => setErr(errMsg(e))); }, [sid]);
  if (err) return <Section><Empty text={err} /></Section>;
  return <Section>{r ? <><p className="text-sm text-zinc-500 mb-3">{fmtDate(r.submitted_at)}</p><Result r={r} /></> : <Loading />}</Section>;
}
